/**
 * Web Analytics Configuration (PostHog via script loader)
 *
 * Platform-specific implementation for web using PostHog's script loader.
 * This approach bypasses Metro bundling issues with posthog-js npm package.
 * Metro bundler automatically selects this file for web builds.
 *
 * Integrates Sentry (error monitoring) + PostHog (product analytics)
 *
 * Sentry Tracing (Web-Specific):
 * - browserTracingIntegration: Auto-instruments page loads, XHR/fetch, Web Vitals
 * - tracePropagationTargets: Enables distributed tracing to Edge Functions
 * - enableInp: Interaction to Next Paint metric (Core Web Vital)
 *
 * @see https://docs.sentry.io/platforms/javascript/tracing/instrumentation/
 */

// ============================================================================
// CRITICAL: Array.at() Polyfill - Must run BEFORE any imports
// ============================================================================
// Sentry's browserTracingIntegration uses web-vitals internally, which calls
// Array.prototype.at() for CLS/FCP metrics. Older browsers (Chrome <92, Safari <15.4)
// don't support this ES2022 method, causing TypeError in Sentry's performance monitoring.
// Sentry Issues: DRAMA-WEB-1T, DRAMA-WEB-1V
if (typeof Array.prototype.at !== 'function') {
  Array.prototype.at = function (index: number) {
    const len = this.length;
    const relativeIndex = index < 0 ? len + index : index;
    if (relativeIndex < 0 || relativeIndex >= len) return undefined;
    return this[relativeIndex];
  };
}

import { logger } from "./logger";
import * as SentryBrowser from "@sentry/browser";
import { consoleLoggingIntegration, browserProfilingIntegration } from "@sentry/browser";
import {
  APP_VERSION,
  SENTRY_ENVIRONMENT,

  shouldInitializeSentry,
  shouldDropSentryEvent,
  getSharedSentryConfig,

  scrubPII,
  scrubBreadcrumb,
  scrubObject,
} from "./analytics/config";

import {
  linkPostHogSession,
  captureErrorShared,
  identifyUserShared,
  clearUserShared,
  getTraceHeadersShared,
} from "./analytics/shared";
import { getPostHogCache } from "./analytics/posthog-cache";

// Import setPlatformTag for internal use (re-exports don't create local bindings)
import { setPlatformTag as setPlatformTagInternal } from "./analytics/context.web";
import { getTelemetryHeaders as getTelemetryHeadersInternal } from "./analytics/telemetry-headers";
import { applyUniversalAnalyticsProperties } from "./analytics/event-contract";
import type { AnalyticsEventName, AnalyticsEventProperties } from "./analytics/types/events";

// Import Metro error detector for runtime error enrichment
import { enrichMetroError } from "./analytics/metro-error-detector";

// Re-export context management functions from platform-specific implementation
export {
  setAnalysisContext,
  setDeviceContext,
  setSessionContext,
  clearAnalysisContext,
  addBreadcrumb,
  startTransaction,
  setTierTags,
  setPlatformTag,
  setBusinessImpactTags,
  setAIContext,
  setQuotaContext,
  setAnalysisErrorContext,
  setRequestIdContext,
} from "./analytics/context.web";

// Re-export PostHog telemetry header propagation (Phase 3 - January 2026)
export { getTelemetryHeaders } from "./analytics/telemetry-headers";

// Re-export PostHog initialization (extracted to keep this file under 500 lines)
export { initPostHog } from "./analytics/posthog-init.web";

// Export Sentry from @sentry/browser for direct access
export { SentryBrowser as Sentry };

// ============================================================================
// TypeScript Types (Web-Specific)
// ============================================================================

declare global {
  interface Window {
    posthog?: any;
  }
}

// ============================================================================
// Sentry Log Noise Filter (NocoDB #1604)
// ============================================================================
// Deny-list of substring patterns to drop from Sentry structured logs.
// These account for ~110k+ noisy events/month (empty messages, framework
// warnings, Cloudflare Turnstile errors, font timeouts, animation warnings).
const SENTRY_LOG_DENY_PATTERNS: string[] = [
  'You have already initialized PostHog',
  '[Cloudflare Turnstile] Error',
  '[TurnstileWebRenderer] Challenge failed',
  '[FontTracking]',
];

// ============================================================================
// Sentry Initialization (Web-Specific)
// ============================================================================

export function initSentry(): void {
  if (!shouldInitializeSentry()) {
    return;
  }

  SentryBrowser.init({
    ...getSharedSentryConfig(),

    // Layer 1 Filtering: Ignore non-actionable client-side network errors
    // @see /home/carlosleivacom/workspace/NETWORK_ERROR_FILTERING_STRATEGY.md
    ignoreErrors: [
      'NetworkError',
      'Network request failed',
      'Failed to fetch',
      'Load failed',
      'AbortError',
      'The user aborted a request',
      'The operation was aborted',
      'net::ERR_NAME_NOT_RESOLVED',
      'net::ERR_CONNECTION_REFUSED',
      'net::ERR_INTERNET_DISCONNECTED',
      // DRAMA-WEB-28: Cloudflare Turnstile invisible mode internal error
      'Error invoking post: Method not found',
    ],

    // NocoDB #1604: Drop empty and noisy log events (~110k/month)
    beforeSendLog(log) {
      const msg = typeof log.message === 'string' ? log.message : '';

      // Drop empty/blank messages (104k+ events/month from framework internals)
      if (!msg.trim()) {
        return null;
      }

      // Drop logs matching deny-list patterns
      for (const pattern of SENTRY_LOG_DENY_PATTERNS) {
        if (msg.includes(pattern)) {
          return null;
        }
      }

      return log;
    },

    beforeSend(event, hint) {
      const enrichedEvent = enrichMetroError(event, { originalException: hint.originalException instanceof Error ? hint.originalException : undefined });

      // DRAMA-WEB-1X: Filter Playwright E2E test errors
      const isPlaywrightError =
        enrichedEvent.exception?.values?.[0]?.stacktrace?.frames?.some(
          (frame: any) => frame.function?.includes('UtilityScript')
        ) ||
        enrichedEvent.exception?.values?.[0]?.value?.includes(':has-text(') ||
        (enrichedEvent.exception?.values?.[0]?.type === 'SyntaxError' &&
          enrichedEvent.exception?.values?.[0]?.value?.includes('querySelector') &&
          enrichedEvent.exception?.values?.[0]?.value?.includes('not a valid selector'));

      if (isPlaywrightError) {
        logger.debug('[Sentry] Filtered Playwright E2E test error (not a production bug)');
        return null;
      }

      // DRAMA-WEB-1W: Filter intentional test errors
      if (enrichedEvent.exception?.values?.[0]?.value?.includes('Simulated')) {
        logger.debug('[Sentry] Filtered simulated test error (intentional E2E test)');
        return null;
      }

      // DRAMA-WEB-1H: defense-in-depth backup for fontfaceobserver timeout
      // (the unhandledrejection listener below is the primary catch).
      // Don't check frame.filename: at beforeSend time it's the bundled
      // entry JS, not the symbolicated 'fontfaceobserver.standalone.js'
      // (DRAMA-WEB-4B post-mortem, NocoDB #4663). Use message regex only.
      if (/\d+ms timeout exceeded/.test(enrichedEvent.exception?.values?.[0]?.value ?? '')) {
        logger.debug('[Sentry] Filtered fontfaceobserver timeout (tracked in PostHog)');
        return null;
      }

      // Filter expected business logic errors (DRAMA-WEB-2S, DRAMA-WEB-2Z)
      // "Quota exceeded" is normal enforcement, not a bug.
      // "Not authenticated: no access token" is a known race condition with graceful fallback.
      const errorValue = enrichedEvent.exception?.values?.[0]?.value ?? '';
      if (errorValue.includes('Quota exceeded') ||
          errorValue.includes('no access token available')) {
        logger.debug('[Sentry] Filtered expected business logic error:', errorValue);
        return null;
      }

      // DRAMA-WEB-1K: Enhanced NetworkError debugging context
      const isNetworkError = enrichedEvent.exception?.values?.[0]?.type === 'NetworkError'
        || enrichedEvent.exception?.values?.[0]?.value?.includes('NetworkError')
        || enrichedEvent.tags?.['DOMException.code'] === '19';

      if (isNetworkError && typeof window !== 'undefined') {
        enrichedEvent.contexts = {
          ...enrichedEvent.contexts,
          network: {
            online: navigator.onLine,
            connection_type: (navigator as any).connection?.effectiveType,
            connection_downlink: (navigator as any).connection?.downlink,
            connection_rtt: (navigator as any).connection?.rtt,
            url_hostname: window.location.hostname,
            url_protocol: window.location.protocol,
            url_pathname: window.location.pathname,
            user_agent: navigator.userAgent,
          },
        };
      }

      if (shouldDropSentryEvent(enrichedEvent)) {
        return null;
      }

      // Scrub PII from exception messages
      if (enrichedEvent.exception?.values) {
        enrichedEvent.exception.values.forEach((exception: any) => {
          if (exception.value) {
            exception.value = scrubPII(exception.value);
          }
        });
      }

      // Scrub PII from breadcrumbs
      if (enrichedEvent.breadcrumbs) {
        enrichedEvent.breadcrumbs = enrichedEvent.breadcrumbs.map(scrubBreadcrumb);
      }

      // Link PostHog user ID to Sentry for correlation
      // Uses cache fallback for when PostHog SDK is still loading (stub mode)
      if (typeof window !== 'undefined' && enrichedEvent.user) {
        const distinctId = window.posthog?.get_distinct_id?.() || getPostHogCache().distinctId;
        if (distinctId) {
          enrichedEvent.user.id = distinctId;
        }
      }

      // Bidirectional Session Replay Linking (Phase 3)
      // Uses cache fallback — get_session_id() returns undefined on the stub
      if (typeof window !== 'undefined') {
        linkPostHogSession(enrichedEvent, () => {
          return window.posthog?.get_session_id?.() || getPostHogCache().sessionId || undefined;
        });
      }

      return enrichedEvent;
    },

    beforeBreadcrumb(breadcrumb) {
      return scrubBreadcrumb(breadcrumb);
    },

    // Web-specific tracing, profiling, and logging configuration
    integrations: [
      SentryBrowser.browserTracingIntegration({
        enableInp: true,
      }),
      // Browser profiling (Chromium-only: Chrome, Edge).
      // No Firefox/Safari support — requires JS Self-Profiling API.
      // Requires Document-Policy: js-profiling header.
      browserProfilingIntegration(),
      // Sentry Structured Logs: Capture console.warn/error/info as searchable logs
      consoleLoggingIntegration({
        levels: ['warn', 'error', 'info'],
      }),
    ],

    // Profiling: sample 10% of traced transactions (Chromium-only: Chrome, Edge)
    profilesSampleRate: 0.1,

    // Session Replay: DISABLED - using PostHog for replays instead
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,

    // Structured logs feature flag — still under _experiments in @sentry/browser v10
    _experiments: {
      enableLogs: true,
    },

    // Distributed tracing: propagate sentry-trace + baggage headers
    tracePropagationTargets: [
      'localhost',
      /^https:\/\/dd-workers.*\.liftping\.workers\.dev/,
      /^https:\/\/api\.dramadetector\.com/,
    ],
  });

  setPlatformTagInternal();

  // DRAMA-WEB-1K & DRAMA-WEB-1Q & DRAMA-WEB-4B: Global unhandled rejection handler
  if (typeof window !== 'undefined') {
    window.addEventListener('unhandledrejection', (event) => {
      const error = event.reason;

      // DRAMA-WEB-4B + DRAMA-WEB-1H: font-loader noise from
      // fontfaceobserver / ExpoFontLoader as unhandled promise rejections.
      // Firefox-only NS_ERROR_FAILURE (interrupted load) and cross-browser
      // "Xms timeout exceeded" both filtered here: error.name + message
      // are reliable without source-map symbolication.
      if (error instanceof Error) {
        const isNsErrorFailure = error.name === 'NS_ERROR_FAILURE';
        const isFontTimeout = /^\d+ms timeout exceeded$/.test(error.message);
        if (isNsErrorFailure || isFontTimeout) {
          logger.debug('[Analytics] Caught font-loader noise (filtered from Sentry):', {
            kind: isNsErrorFailure ? 'firefox_ns_error_failure' : 'fontfaceobserver_timeout',
            message: error.message,
            userAgent: navigator.userAgent,
          });
          event.preventDefault();
          return;
        }
      }

      // DRAMA-WEB-1K: NetworkError (DOMException code 19)
      if (error instanceof DOMException && error.name === 'NetworkError') {
        logger.debug('[Analytics] NetworkError caught (filtered from Sentry):', {
          message: error.message,
          code: (error as any).code,
          online: navigator.onLine,
          url: window.location.href,
        });
        event.preventDefault();
        return;
      }

      // DRAMA-WEB-1Q: AbortError from Supabase auth locks
      if (error instanceof Error && error.name === 'AbortError') {
        const isAuthLock = error.stack?.includes('@supabase/auth-js') ||
                          error.stack?.includes('locks.js') ||
                          error.message?.includes('signal is aborted');

        if (isAuthLock) {
          logger.debug('[Analytics] Caught Supabase auth lock AbortError (benign):', {
            message: error.message,
            userAgent: navigator.userAgent,
          });
          event.preventDefault();
          return;
        }

        logger.warn('[Analytics] Caught unhandled AbortError:', error.message);
        SentryBrowser.captureException(error, {
          tags: {
            'error.category': 'abort',
            'error.handled': 'global_handler',
          },
        });
        event.preventDefault();
      }
    });
  }

  logger.log(`[Analytics] Sentry initialized (web) - env: ${SENTRY_ENVIRONMENT}, release: dramadetector@${APP_VERSION}, tracing: enabled, profiling: 0.1, structured logs: enabled, session replay: disabled (PostHog-linked), PII scrubbing: enabled, global error handlers: enabled`);
}

// ============================================================================
// Analytics API (Delegates to shared logic with web adapters)
// ============================================================================

/**
 * Helper to create a PostHogAdapter from window.posthog when available.
 */
function getPostHogAdapter(): { capture: any; identify: any; reset: any } | null {
  if (typeof window !== 'undefined' && window.posthog) {
    return window.posthog;
  }
  return null;
}

/**
 * Capture error and link Sentry + PostHog with optional requestId for E2E correlation
 */
export function captureError(
  error: Error,
  context?: Record<string, any>,
  requestId?: string
): string | undefined {
  return captureErrorShared(SentryBrowser, getPostHogAdapter(), error, context, requestId);
}

/**
 * Capture custom event in PostHog
 *
 * Typed overload: when eventName is a known AnalyticsEventName, properties are type-checked.
 * Fallback: any string with Record<string, unknown> properties (legacy/ad-hoc events).
 *
 * Session Linkage (NocoDB #901):
 * All events receive universal properties: $session_id, environment, platform, app_version.
 * This ensures PostHog session replay correlates every event to the client session.
 *
 * Defense-in-depth strategy (DRAMA-WEB-23):
 * 1. Check PostHog exists
 * 2. Verify initialization started (__initialized flag)
 * 3. Check capture method is available (stub or fully loaded SDK)
 */
export function captureEvent<TEvent extends AnalyticsEventName>(
  eventName: TEvent,
  properties?: AnalyticsEventProperties<TEvent>
): void;
export function captureEvent(
  eventName: string,
  properties?: Record<string, unknown>
): void;
export function captureEvent(
  eventName: string,
  properties?: Record<string, unknown>
): void {
  const telemetry = getTelemetryHeadersInternal();
  const normalizedProperties = applyUniversalAnalyticsProperties({
    eventName,
    properties,
    defaults: {
      sessionId: telemetry['x-posthog-session-id'],
      environment: SENTRY_ENVIRONMENT,
      platform: 'web',
      appVersion: APP_VERSION,
    },
  });

  if (typeof window === 'undefined' || !window.posthog) {
    logger.warn('[Analytics] PostHog not initialized, cannot capture: ' + eventName);
    return;
  }

  if (!(window.posthog as any).__initialized) {
    if (__DEV__) {
      logger.debug('[Analytics] PostHog not yet initialized, event queued via stub: ' + eventName);
    }
  }

  if (typeof window.posthog.capture === 'function') {
    window.posthog.capture(eventName, scrubObject(normalizedProperties));
  } else {
    logger.warn('[Analytics] PostHog capture method not available: ' + eventName);
  }
}

/**
 * Identify user in both analytics systems
 */
export function identifyUser(userId: string, traits?: Record<string, any>): void {
  identifyUserShared(SentryBrowser, getPostHogAdapter(), userId, traits);
}

/**
 * Clear user identification (on logout)
 */
export function clearUser(): void {
  clearUserShared(SentryBrowser, getPostHogAdapter());
}

/**
 * Get PostHog client for advanced usage
 */
export function getPostHog(): any {
  return typeof window !== 'undefined' ? window.posthog : undefined;
}

/**
 * Get Sentry trace headers for distributed tracing (Web Implementation)
 *
 * Uses @sentry/browser getTraceData() for sentry-trace and baggage headers.
 *
 * @see packages/drama-detector/supabase/functions/_shared/utils/sentry-edge.ts#extractTraceHeaders
 */
export function getTraceHeaders(): Record<string, string> {
  return getTraceHeadersShared(() => SentryBrowser.getTraceData());
}
